// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';

// ** Components
import MediaCarousel, { type MediaCarouselItem } from './MediaCarousel';

// ** Utils
import { handleEventImage } from '@/utils/domain/eventUtils';
import { normalizeDateLabel } from '@/utils/shared/stringUtils';
import { useGetCarouselMediaItems } from '@/lib/utils/carouselDataUtils';
import { buildPathFromRelationLink } from '@/utils/ui/navigationUtils';

// ** Types
import type { StrapiComponentElementProps } from '@/types/strapi';
import type { Page, Query, ComponentBlocksCarouselBlock } from '@/types/graphql';

type MediaCarouselVariantProps = StrapiComponentElementProps<Omit<ComponentBlocksCarouselBlock, 'carouselType'>> & {
	mediaTypes?: string[];
	carouselSectionTitle?: string;
	landingPage?: Page | null;
	extraProps?: {
		newsBasePath?: string;
		eventsBasePath?: string;
	};
};

type CarouselDataItem = NonNullable<NonNullable<Query['carouselMediaItems']>['data']>[number];

const MediaCarosuelVariant = ({
	locale,
	landingPage,
	mediaTypes = [],
	carouselShowCta,
	carouselCta,
	carouselShowTitle,
	carouselSectionTitle,
	carouselShowArrowControls,
	extraProps
}: MediaCarouselVariantProps) => {
	const { carouselMediaItems, loading } = useGetCarouselMediaItems(locale ?? 'de', mediaTypes);

	const carouselItems: MediaCarouselItem[] = React.useMemo(() => {
		if (!carouselMediaItems) return [];

		return carouselMediaItems.map((item: CarouselDataItem): MediaCarouselItem => {
			const cleanDate = normalizeDateLabel(item?.publishedAt ? `${item.publishedAt}` : undefined);

			// 1. Identify Type
			const rawType = item?.type?.toLowerCase() || 'news';
			const isEvent = rawType === 'event';

			// Check for news-related types (Perspektiven, Podcast, News)
			const isNewsRelated = ['news', 'podcast', 'perspektiven'].includes(rawType);

			// 2. Resolve specific Base Path
			let itemBasePath = '/';

			if (landingPage) {
				itemBasePath = buildPathFromRelationLink(landingPage);
			} else if (isEvent) {
				itemBasePath = extraProps?.eventsBasePath ?? '';
			} else if (isNewsRelated) {
				itemBasePath = extraProps?.newsBasePath ?? '';
			}

			const finalImageUrl = isEvent ? handleEventImage(item?.typeLabel || 'Seminar') : item?.imageUrl;

			return {
				documentId: item?.documentId ?? '',
				title: item?.title ?? '',
				type: item?.type ?? 'News',
				slug: item?.slug ?? '',
				host: item?.carouselHost ?? '',
				description: item?.description,
				imageUrl: finalImageUrl,
				imageData: item?.imageData as any,
				typeLabel: item?.typeLabel,
				publishedAt: cleanDate,
				ctaLabel: 'Mehr erfahren',
				basePath: itemBasePath
			};
		});
	}, [carouselMediaItems, landingPage, extraProps]);

	const carouselHref = React.useMemo(() => {
		if (carouselCta?.hrefExternal) return carouselCta.hrefExternal;

		const manualLink = buildPathFromRelationLink(carouselCta?.href);
		if (manualLink !== '/') return manualLink;

		if (landingPage) return buildPathFromRelationLink(landingPage);

		// Fallback for "See All" button when no specific page is set
		return extraProps?.newsBasePath || '/';
	}, [carouselCta, landingPage, extraProps]);

	return (
		<React.Fragment>
			{!loading && carouselItems.length > 0 ? (
				<MediaCarousel
					items={carouselItems}
					cardsPerView={{ xs: 1.2, md: 2.5, lg: 3.5 }}
					title={carouselSectionTitle}
					showTitle={carouselShowTitle}
					ctaLabel={carouselCta?.label || 'Alle ansehen'}
					ctaHref={carouselHref}
					showCta={carouselShowCta}
					showArrowControls={carouselShowArrowControls}
					prevAriaLabel={'Vorherige News'}
					nextAriaLabel={'Naechste News'}
					basePath={carouselHref}
				/>
			) : (
				// TODO: Create a Skeleton Component
				loading && <Box sx={{ height: 300, bgcolor: 'grey.100', borderRadius: 2 }} />
			)}
		</React.Fragment>
	);
};

export default MediaCarosuelVariant;
